/**
 * TimeAudit — "Day Audit" sub-page of the Coaching tab. Passive time
 * tracking via ActivityWatch (local server on :5600) with manual interval
 * check-ins as fallback. At end of day Claude clusters the day into
 * blocks, categorizes them, and produces a kill/delegate/keep + hire
 * signal report through Jai's coaching frame.
 *
 * Flow:
 *   1. Page checks if ActivityWatch is reachable at http://localhost:5600.
 *   2. If not — shows install wizard (download AW + run the CORS .bat).
 *   3. If connected — "Sync today" pulls window+web events from AW and
 *      posts them to /api/time-audit?action=ingest-events.
 *   4. Manual interval check-ins always available (notification + modal at
 *      every interval, with mic via Web Speech API).
 *   5. "Audit this day" calls Claude → renders summary panel.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../App";
import { apiFetch } from "../lib/apiFetch";

const API = "/api/time-audit";
// Canonical SR&ED category name — must match RND_CATEGORY in api/time-audit.js.
const RND_CATEGORY = "R&D / Experimental Development";
const AW_BASE = "http://localhost:5600";
const AW_PROBE_TIMEOUT_MS = 1500;
const SETUP_BAT_URL = "/elecbid-aw-setup.bat";
const SETUP_PS1_URL = "/elecbid-aw-setup.ps1";
const SETUP_CMD_URL = "/elecbid-aw-setup.command";
const AUTOSYNC_BAT_URL = "/elecbid-aw-autosync.bat";

function detectOS() {
  if (typeof navigator === "undefined") return "windows";
  const ua = (navigator.userAgent || "").toLowerCase();
  const platform = ((navigator.userAgentData?.platform) || navigator.platform || "").toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return "ios";
  if (/android/.test(ua)) return "android";
  if (/mac/.test(platform) || /mac os/.test(ua)) return "mac";
  if (/linux/.test(platform) || /linux/.test(ua)) return "linux";
  return "windows";
}

// ── Helpers ────────────────────────────────────────────────────────────────
function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtMin(min) {
  const m = Math.round(Number(min) || 0);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60), r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}
function fmtSecAsHM(sec) {
  return fmtMin(Math.round((Number(sec) || 0) / 60));
}
function fmtTimeShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" });
}
function fmtAgo(iso) {
  if (!iso) return "never";
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
function fmtDateLong(yyyymmdd) {
  if (!yyyymmdd) return "—";
  const [y, m, d] = yyyymmdd.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-CA", {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
}
function dayStartISO(yyyymmdd) {
  const [y, m, d] = yyyymmdd.split("-").map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
}
function dayEndISO(yyyymmdd) {
  const [y, m, d] = yyyymmdd.split("-").map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
}
async function fetchWithTimeout(url, opts = {}, timeoutMs = AW_PROBE_TIMEOUT_MS) {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const r = await fetch(url, { ...opts, signal: ctl.signal });
    return r;
  } finally { clearTimeout(t); }
}

// ── Web Speech API helper ──────────────────────────────────────────────────
function getSR() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function TimeAudit() {
  const { session: authSession } = useSession();
  const navigate = useNavigate();
  const userEmail = (authSession?.user?.email || "").toLowerCase();

  const [day, setDay] = useState(() => todayLocal());
  const [session, setSession] = useState(null);
  const [events, setEvents] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [slots, setSlots] = useState([]);
  const [categories, setCategories] = useState([]);
  const [awStatus, setAwStatus] = useState({ connected: null, hostname: null, version: null, lastCheck: null });
  const [devices, setDevices] = useState(null); // null = not loaded yet, [] = none

  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [status, setStatusMsg] = useState("");

  const [showSetup, setShowSetup] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [slotModal, setSlotModal] = useState(null); // { slot_index, slot_start, slot_end, existing }
  const [recentSessions, setRecentSessions] = useState([]);
  const [trendOpen, setTrendOpen] = useState(false);
  const [trendBusy, setTrendBusy] = useState(false);
  const [drives, setDrives] = useState([]);
  const [rndNote, setRndNote] = useState(null); // { note_text, projects } for the current day

  const [targetHours, setTargetHours] = useState(8);
  const [intervalMin, setIntervalMin] = useState(60);
  const [pingsEnabled, setPingsEnabled] = useState(false);

  const pingTimerRef = useRef(null);
  const nextPingAtRef = useRef(null);
  const [nextPingIn, setNextPingIn] = useState(null);

  // ── AW Connection probe ──────────────────────────────────────────────────
  const checkAW = useCallback(async () => {
    try {
      const r = await fetchWithTimeout(`${AW_BASE}/api/0/info`);
      if (!r.ok) throw new Error(`AW ${r.status}`);
      const info = await r.json();
      setAwStatus({
        connected: true,
        hostname: info.hostname || null,
        version: info.version || null,
        lastCheck: new Date().toISOString(),
      });
      return true;
    } catch {
      setAwStatus(prev => ({ ...prev, connected: false, lastCheck: new Date().toISOString() }));
      return false;
    }
  }, []);

  // Auto-probe on mount + every 10s while setup wizard is open
  useEffect(() => {
    checkAW();
    const id = setInterval(() => { if (showSetup) checkAW(); }, 5000);
    return () => clearInterval(id);
  }, [checkAW, showSetup]);

  // ── Initial setup (seed categories) ──────────────────────────────────────
  useEffect(() => {
    if (!userEmail) return;
    apiFetch(API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "setup", user_email: userEmail }),
    }).catch(() => {});
  }, [userEmail]);

  // ── Computers panel: which machines have synced AW data recently ─────────
  const loadDevices = useCallback(async () => {
    if (!userEmail) return;
    try {
      const r = await apiFetch(`${API}?action=list-devices&user=${encodeURIComponent(userEmail)}`).then(r => r.json());
      if (r.ok) setDevices(r.devices || []);
    } catch { /* informational panel — never block the page on it */ }
  }, [userEmail]);
  // Refresh every 5 min while open — other machines' agents sync hourly and
  // a just-wired computer should appear without a manual page reload.
  useEffect(() => {
    loadDevices();
    const id = setInterval(loadDevices, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [loadDevices]);

  // ── Load session + dependents whenever day changes ───────────────────────
  const reloadSession = useCallback(async () => {
    if (!userEmail || !day) return;
    setBusy("loading"); setError("");
    try {
      const r = await apiFetch(`${API}?action=session&user=${encodeURIComponent(userEmail)}&day=${day}`).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Failed to load session");
      setSession(r.session || null);
      if (r.session) {
        setTargetHours(Number(r.session.target_hours) || 8);
        setIntervalMin(Number(r.session.interval_minutes) || 60);
        await Promise.all([
          loadEvents(r.session.id),
          loadBlocks(r.session.id),
          loadSlots(r.session.id),
          loadDrives(r.session.id),
        ]);
      } else {
        setEvents([]); setBlocks([]); setSlots([]); setDrives([]);
      }
      // Always reload categories
      const c = await apiFetch(`${API}?action=list-categories&user=${encodeURIComponent(userEmail)}`).then(r => r.json());
      if (c.ok) setCategories(c.categories || []);
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }, [userEmail, day]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { reloadSession(); }, [reloadSession]);

  // ── Load the day's R&D micro-journal note whenever the day changes ────────
  const reloadRndNote = useCallback(async () => {
    if (!userEmail || !day) return;
    try {
      const r = await apiFetch(`${API}?action=get-rnd-note&user=${encodeURIComponent(userEmail)}&day=${day}`).then(r => r.json());
      if (r.ok) setRndNote(r.note || null);
    } catch { /* swallow — card still renders empty */ }
  }, [userEmail, day]);
  useEffect(() => { reloadRndNote(); }, [reloadRndNote]);

  // Save the daily R&D note, then re-run the audit so the note feeds the
  // SR&ED narratives (rd fields) the AI writes for that day's R&D blocks.
  async function saveRndNote({ note_text, projects }) {
    if (!userEmail || !day) return;
    setBusy("saving-rnd"); setError("");
    try {
      const r = await apiFetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "upsert-rnd-note", user_email: userEmail, day, note_text, projects }),
      }).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Could not save note");
      setRndNote(r.note || null);
      setStatusMsg("R&D note saved.");
      // Re-audit so the note is woven into the rd narratives (only if there's a
      // session with something to audit).
      if (session && (events.length || slots.length)) {
        setBusy("auditing");
        await apiFetch(API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "audit-day", session_id: session.id }),
        });
        await Promise.all([loadBlocks(session.id), reloadSession()]);
      }
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }

  // One-shot morning sync-and-audit for YESTERDAY when the page opens.
  // The nightly cron can't pull from the user's local AW — only the browser
  // can. So when Dylan opens Day Audit in the morning, we silently pull
  // yesterday's AW events too (not just today's), and if any new data
  // arrived since the last audit ran, trigger a silent audit. Coffee →
  // yesterday's full audit ready by the time he looks.
  const morningCatchUpRef = useRef(false);
  useEffect(() => {
    if (morningCatchUpRef.current) return;
    if (!userEmail || !awStatus.connected) return;
    if (busy) return;
    morningCatchUpRef.current = true;
    (async () => {
      const today = todayLocal();
      const [y, m, d] = today.split("-").map(Number);
      const yDate = new Date(y, m - 1, d - 1);
      const yesterday = `${yDate.getFullYear()}-${String(yDate.getMonth() + 1).padStart(2, "0")}-${String(yDate.getDate()).padStart(2, "0")}`;
      try {
        // 1. Get-or-create yesterday's session.
        const sessRes = await apiFetch(`${API}?action=session&user=${encodeURIComponent(userEmail)}&day=${yesterday}`).then(r => r.json());
        let ySession = sessRes.session;
        if (!ySession) return; // No yesterday session — nothing to catch up.

        // 2. Silent-sync yesterday from AW.
        await syncDayFromAW(yesterday, ySession.id);

        // 3. Check if events count is non-trivial and the latest summary is older
        // than the latest event. If so, run audit silently.
        const eventsRes = await apiFetch(`${API}?action=list-events&session_id=${ySession.id}`).then(r => r.json());
        const hasEvents = (eventsRes.events || []).length > 0;
        const sessAfter = await apiFetch(`${API}?action=session&user=${encodeURIComponent(userEmail)}&day=${yesterday}`).then(r => r.json());
        const sessFresh = sessAfter.session;
        const needsAudit = hasEvents && (!sessFresh?.summary_json || new Date(sessFresh.updated_at).getTime() < Date.now() - 60_000);
        if (needsAudit) {
          await apiFetch(API, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "audit-day", session_id: ySession.id }),
          });
          setStatusMsg(`Yesterday's audit refreshed automatically with new evening data.`);
          // If user is currently viewing yesterday, reload it
          if (day === yesterday) await reloadSession();
        }
      } catch {
        /* silent — manual buttons still work */
      }
    })();
  }, [userEmail, awStatus.connected]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper: pull AW events for any given day into its session.
  async function syncDayFromAW(yyyymmdd, sessionId) {
    const bucketsRes = await fetchWithTimeout(`${AW_BASE}/api/0/buckets/`, {}, 5000);
    if (!bucketsRes.ok) return;
    const buckets = await bucketsRes.json();
    const wanted = Object.values(buckets).filter(b => {
      const t = (b.type || "") + " " + (b.id || "");
      return /currentwindow|web\.tab|android\.app|afkstatus/.test(t);
    });
    if (!wanted.length) return;
    const start = dayStartISO(yyyymmdd);
    const end = dayEndISO(yyyymmdd);
    const all = [];
    for (const b of wanted) {
      const url = `${AW_BASE}/api/0/buckets/${encodeURIComponent(b.id)}/events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}&limit=10000`;
      const r = await fetchWithTimeout(url, {}, 8000);
      if (!r.ok) continue;
      const rows = await r.json();
      const isWeb = /web\.tab/.test(b.type || "");
      const isAfk = /afkstatus/.test(b.type || "");
      const isAndroid = /android/.test(b.type || "") || /android/.test(b.id || "");
      for (const ev of rows) {
        if (isAfk && ev.data?.status === "afk") continue;
        if (Number(ev.duration) < 5) continue;
        all.push({
          started_at: ev.timestamp,
          ended_at: new Date(new Date(ev.timestamp).getTime() + Number(ev.duration) * 1000).toISOString(),
          duration_sec: Math.round(Number(ev.duration)),
          app: ev.data?.app || ev.data?.application || (isWeb ? "browser" : null),
          title: ev.data?.title || ev.data?.url || null,
          url: ev.data?.url || null,
          source: isAndroid ? "aw-android" : (isWeb ? "aw-web" : (isAfk ? "aw-afk" : "aw-window")),
          hostname: awStatus.hostname || null,
        });
      }
    }
    if (!all.length) return;
    await apiFetch(API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "ingest-events",
        session_id: sessionId,
        hostname: awStatus.hostname || null,
        events: all,
      }),
    });
  }

  // Auto-start today's session once AW is detected and we land on today with
  // no session yet. Means a user who forgot to click "Start Day" still has a
  // session waiting at end of day when they hit Sync.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (autoStartedRef.current) return;
    if (!userEmail || !day || day !== todayLocal()) return;
    if (session) return;
    if (awStatus.connected !== true) return;
    if (busy) return;
    autoStartedRef.current = true;
    (async () => {
      try {
        const r = await apiFetch(API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "start-session",
            user_email: userEmail,
            day,
            target_hours: targetHours,
            interval_minutes: intervalMin,
            source: "activitywatch",
            hostname: awStatus.hostname || null,
          }),
        }).then(r => r.json());
        if (r.ok && r.session) {
          setSession(r.session);
          setStatusMsg("Today's session started automatically (AW is connected).");
        }
      } catch { /* swallow — manual Start button still works */ }
    })();
  }, [userEmail, day, session, awStatus.connected, awStatus.hostname, busy, targetHours, intervalMin]);

  async function loadEvents(sessionId) {
    const r = await apiFetch(`${API}?action=list-events&session_id=${sessionId}`).then(r => r.json());
    if (r.ok) setEvents(r.events || []);
  }
  async function loadBlocks(sessionId) {
    const r = await apiFetch(`${API}?action=list-blocks&session_id=${sessionId}`).then(r => r.json());
    if (r.ok) setBlocks(r.blocks || []);
  }
  async function loadSlots(sessionId) {
    const r = await apiFetch(`${API}?action=list-slots&session_id=${sessionId}`).then(r => r.json());
    if (r.ok) setSlots(r.slots || []);
  }
  async function loadDrives(sessionId) {
    const r = await apiFetch(`${API}?action=list-drives&session_id=${sessionId}`).then(r => r.json());
    if (r.ok) setDrives(r.drives || []);
  }

  // ── Start / resume the day's session ─────────────────────────────────────
  async function startDay() {
    if (!userEmail) return;
    setBusy("starting"); setError("");
    try {
      const r = await apiFetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "start-session",
          user_email: userEmail,
          day,
          target_hours: targetHours,
          interval_minutes: intervalMin,
          source: awStatus.connected ? "activitywatch" : "manual",
          hostname: awStatus.hostname || null,
        }),
      }).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Could not start session");
      setSession(r.session);
      setStatusMsg("Day started. Good luck.");
      if (typeof Notification !== "undefined" && Notification.permission === "default") {
        try { await Notification.requestPermission(); } catch {}
      }
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }

  // ── Sync from ActivityWatch (manual or silent auto) ──────────────────────
  const [lastAutoSyncAt, setLastAutoSyncAt] = useState(null);

  async function runSyncFromAW({ silent = false } = {}) {
    if (!session) {
      if (!silent) setError("Start the day first.");
      return;
    }
    if (!awStatus.connected) {
      if (!silent) setError("ActivityWatch isn't connected. Run the setup.");
      return;
    }
    if (!silent) { setBusy("syncing"); setError(""); setStatusMsg(""); }
    try {
      // 1. List buckets, pick the watcher buckets we care about.
      const bucketsRes = await fetchWithTimeout(`${AW_BASE}/api/0/buckets/`, {}, 5000);
      if (!bucketsRes.ok) throw new Error(`AW buckets ${bucketsRes.status}`);
      const buckets = await bucketsRes.json();
      const wanted = Object.values(buckets).filter(b => {
        const t = (b.type || "") + " " + (b.id || "");
        return /currentwindow|web\.tab|android\.app|afkstatus/.test(t);
      });
      if (!wanted.length) throw new Error("No ActivityWatch watcher buckets found.");

      // 2. Pull events for [start, end] of the day.
      const start = dayStartISO(day);
      const end = dayEndISO(day);
      const all = [];
      for (const b of wanted) {
        const url = `${AW_BASE}/api/0/buckets/${encodeURIComponent(b.id)}/events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}&limit=10000`;
        const r = await fetchWithTimeout(url, {}, 8000);
        if (!r.ok) continue;
        const rows = await r.json();
        const isWeb = /web\.tab/.test(b.type || "");
        const isAfk = /afkstatus/.test(b.type || "");
        const isAndroid = /android/.test(b.type || "") || /android/.test(b.id || "");
        for (const ev of rows) {
          if (isAfk && ev.data?.status === "afk") continue;
          if (Number(ev.duration) < 5) continue;
          all.push({
            started_at: ev.timestamp,
            ended_at: new Date(new Date(ev.timestamp).getTime() + Number(ev.duration) * 1000).toISOString(),
            duration_sec: Math.round(Number(ev.duration)),
            app: ev.data?.app || ev.data?.application || (isWeb ? "browser" : null),
            title: ev.data?.title || ev.data?.url || null,
            url: ev.data?.url || null,
            source: isAndroid ? "aw-android" : (isWeb ? "aw-web" : (isAfk ? "aw-afk" : "aw-window")),
            hostname: awStatus.hostname || null,
          });
        }
      }
      if (!all.length) {
        if (!silent) setStatusMsg("No ActivityWatch data found for this day yet.");
        setLastAutoSyncAt(new Date());
        return;
      }

      // 3. Ship to the server.
      const ingest = await apiFetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "ingest-events",
          session_id: session.id,
          hostname: awStatus.hostname || null,
          events: all,
        }),
      }).then(r => r.json());
      if (!ingest.ok) throw new Error(ingest.error || "Ingest failed");
      setLastAutoSyncAt(new Date());
      loadDevices();
      if (!silent) {
        setStatusMsg(`Synced ${ingest.inserted} events from ActivityWatch.`);
      }
      await loadEvents(session.id);
      if (!silent) await reloadSession();
    } catch (e) {
      if (!silent) setError(`Sync failed: ${e.message}`);
    } finally {
      if (!silent) setBusy("");
    }
  }

  const syncFromAW = () => runSyncFromAW({ silent: false });

  // Periodic auto-sync — only when today's session is loaded, AW is connected,
  // and the user isn't in the middle of a manual operation. 5-minute cadence
  // keeps the server's event log fresh so the nightly cron audit can run
  // immediately without needing a manual sync.
  useEffect(() => {
    if (!session || day !== todayLocal() || !awStatus.connected) return;
    // First sync ~3 sec after page settles — short enough that even a brief
    // tab visit captures the day's AW data into the server (so the midnight
    // cron has something to audit even if you didn't keep the tab open).
    const initialDelay = setTimeout(() => runSyncFromAW({ silent: true }), 3 * 1000);
    const id = setInterval(() => {
      if (!busy) runSyncFromAW({ silent: true });
    }, 5 * 60 * 1000);
    return () => { clearTimeout(initialDelay); clearInterval(id); };
  }, [session?.id, day, awStatus.connected]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Run the AI audit ─────────────────────────────────────────────────────
  async function runAudit() {
    if (!session) { setError("Start the day first."); return; }
    setBusy("auditing"); setError(""); setStatusMsg("");
    try {
      const r = await apiFetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "audit-day", session_id: session.id }),
      }).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Audit failed");
      setStatusMsg(`Audit complete — ${r.blocks_written} blocks.`);
      await Promise.all([loadBlocks(session.id), reloadSession()]);
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }

  // ── Backfill missing days ────────────────────────────────────────────────
  // The nightly cron only audits sessions that EXIST for "yesterday". Sessions
  // only get created when the user has the Day Audit tab open with AW
  // connected. If a tab-less stretch happens, those days never make it to the
  // server. This loop walks the local AW history and catches the server up.
  const [backfillProgress, setBackfillProgress] = useState(null);

  const missingDays = useMemo(() => {
    if (!recentSessions.length) return [];
    const sortedAsc = [...recentSessions].map(s => s.day).sort();
    const earliest = sortedAsc[0];
    const today = todayLocal();
    const [ty, tm, td] = today.split("-").map(Number);
    const yest = new Date(ty, tm - 1, td);
    yest.setDate(yest.getDate() - 1);
    const yestStr = `${yest.getFullYear()}-${String(yest.getMonth() + 1).padStart(2, "0")}-${String(yest.getDate()).padStart(2, "0")}`;
    if (earliest >= yestStr) return [];
    const have = new Set(recentSessions.map(s => s.day));
    const out = [];
    const [ey, em, ed] = earliest.split("-").map(Number);
    const cur = new Date(ey, em - 1, ed);
    cur.setDate(cur.getDate() + 1); // start day after earliest
    while (cur <= yest) {
      const d = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(cur.getDate()).padStart(2, "0")}`;
      if (!have.has(d)) out.push(d);
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }, [recentSessions]);

  async function backfillMissingDays() {
    if (!awStatus.connected) {
      setError("ActivityWatch isn't connected. Backfill pulls history from your local AW — start it first.");
      return;
    }
    if (!missingDays.length) return;
    setError(""); setStatusMsg("");
    setBackfillProgress({ total: missingDays.length, done: 0, current: missingDays[0], phase: "starting" });
    let succeeded = 0;
    let emptied = 0;
    for (let i = 0; i < missingDays.length; i++) {
      const d = missingDays[i];
      setBackfillProgress({ total: missingDays.length, done: i, current: d, phase: "syncing" });
      try {
        const sRes = await apiFetch(API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "start-session",
            user_email: userEmail,
            day: d,
            target_hours: 8,
            interval_minutes: 60,
            source: "activitywatch",
            hostname: awStatus.hostname || null,
          }),
        }).then(r => r.json());
        if (!sRes.ok || !sRes.session) continue;
        await syncDayFromAW(d, sRes.session.id);
        // Skip audit if AW had no events for that day (e.g. weekend, machine off).
        const evRes = await apiFetch(`${API}?action=list-events&session_id=${sRes.session.id}`).then(r => r.json());
        const evCount = (evRes.events || []).length;
        if (evCount === 0) { emptied++; continue; }
        setBackfillProgress({ total: missingDays.length, done: i, current: d, phase: "auditing" });
        await apiFetch(API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "audit-day", session_id: sRes.session.id }),
        });
        succeeded++;
      } catch (e) {
        console.warn(`Backfill ${d} failed:`, e.message);
      }
    }
    setBackfillProgress({ total: missingDays.length, done: missingDays.length, current: null, phase: "done" });
    await loadTrend();
    setStatusMsg(`Backfill complete — audited ${succeeded} day${succeeded === 1 ? "" : "s"}${emptied ? ` (${emptied} had no AW data)` : ""}.`);
    setTimeout(() => setBackfillProgress(null), 6000);
  }

  // ── Multi-day trend ──────────────────────────────────────────────────────
  // 60-day window so the missing-days detector (below) can see across longer
  // gaps — e.g. user didn't open the tab for two weeks while travelling.
  const loadTrend = useCallback(async () => {
    if (!userEmail) return;
    setTrendBusy(true);
    try {
      const r = await apiFetch(`${API}?action=list-recent-sessions&user=${encodeURIComponent(userEmail)}&days=60`).then(r => r.json());
      if (r.ok) setRecentSessions(r.sessions || []);
    } catch { /* swallow */ }
    finally { setTrendBusy(false); }
  }, [userEmail]);

  // Auto-load trend on mount so the backfill banner can surface missing days
  // without the user having to open the trend panel first.
  useEffect(() => {
    if (userEmail) loadTrend();
  }, [userEmail, loadTrend]);

  // ── Manual slot upsert ───────────────────────────────────────────────────
  async function upsertSlot(payload) {
    setBusy("saving-slot"); setError("");
    try {
      const r = await apiFetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "upsert-slot", ...payload }),
      }).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Could not save slot");
      await loadSlots(session.id);
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }

  // ── Interval pings (manual mode kitchen timer) ───────────────────────────
  const scheduleNextPing = useCallback(() => {
    if (pingTimerRef.current) clearTimeout(pingTimerRef.current);
    if (!pingsEnabled || !session) return;
    const delay = Math.max(15, intervalMin) * 60 * 1000;
    const at = new Date(Date.now() + delay);
    nextPingAtRef.current = at;
    pingTimerRef.current = setTimeout(() => {
      firePing();
      scheduleNextPing();
    }, delay);
  }, [pingsEnabled, session, intervalMin]); // eslint-disable-line react-hooks/exhaustive-deps

  function firePing() {
    if (!session) return;
    const now = new Date();
    const start = new Date(now.getTime() - intervalMin * 60 * 1000);
    const idx = Math.max(0, Math.round((now - new Date(session.created_at || dayStartISO(day))) / (intervalMin * 60 * 1000)));
    setSlotModal({
      slot_index: idx,
      slot_start: start.toISOString(),
      slot_end: now.toISOString(),
      existing: null,
    });
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      try {
        new Notification("ElecBid — Day Audit", {
          body: `What did you do in the last ${intervalMin} min?`,
          icon: "/elecbid-icon-128.png",
        });
      } catch {}
    }
  }

  // Update the "next ping in" countdown
  useEffect(() => {
    if (!pingsEnabled) { setNextPingIn(null); return; }
    const id = setInterval(() => {
      const at = nextPingAtRef.current;
      if (!at) { setNextPingIn(null); return; }
      const ms = at.getTime() - Date.now();
      setNextPingIn(ms > 0 ? Math.ceil(ms / 1000) : 0);
    }, 1000);
    return () => clearInterval(id);
  }, [pingsEnabled]);

  // Start / stop the interval ping loop
  useEffect(() => {
    if (pingsEnabled && session) {
      scheduleNextPing();
    } else if (pingTimerRef.current) {
      clearTimeout(pingTimerRef.current);
      pingTimerRef.current = null;
      nextPingAtRef.current = null;
    }
    return () => { if (pingTimerRef.current) clearTimeout(pingTimerRef.current); };
  }, [pingsEnabled, session, scheduleNextPing]);

  function openManualLog() {
    if (!session) return;
    const now = new Date();
    const start = new Date(now.getTime() - intervalMin * 60 * 1000);
    const idx = slots.length;
    setSlotModal({
      slot_index: idx,
      slot_start: start.toISOString(),
      slot_end: now.toISOString(),
      existing: null,
    });
  }

  // ── Derived: totals by category (from blocks, fallback to slots) ─────────
  const totals = useMemo(() => {
    const sumByCat = {};
    if (session?.totals_json && Object.keys(session.totals_json).length) {
      for (const [k, v] of Object.entries(session.totals_json)) sumByCat[k] = Number(v) || 0;
    } else {
      for (const b of blocks) {
        const c = b.category || "Other";
        sumByCat[c] = (sumByCat[c] || 0) + Math.round((b.duration_sec || 0) / 60);
      }
    }
    return sumByCat;
  }, [blocks, session]);

  const totalLoggedMin = useMemo(() => {
    return Object.values(totals).reduce((s, v) => s + (Number(v) || 0), 0);
  }, [totals]);

  const rndMinutes = useMemo(() => Math.round(Number(totals[RND_CATEGORY]) || 0), [totals]);

  const colorFor = useCallback((catName) => {
    return categories.find(c => c.name === catName)?.color || "#6b7280";
  }, [categories]);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <button onClick={() => navigate("/coach")} style={S.backBtn}>‹ Back to Coach</button>
          <div style={S.title}>Day Audit</div>
          <div style={S.subtitle}>Where did your 8h actually go? Coached by Jai using EOS, E-Myth, and Service Loop Electrical.</div>
        </div>
        <div style={S.headerActions}>
          <input
            type="date"
            value={day}
            max={todayLocal()}
            onChange={e => setDay(e.target.value)}
            style={S.dateInput}
            disabled={!!busy}
          />
          <button onClick={() => navigate("/coach/rnd")} style={S.btn} disabled={!!busy}>SR&amp;ED report ›</button>
          <button onClick={() => setShowSettings(true)} style={S.btn} disabled={!!busy}>Settings</button>
        </div>
      </div>

      {error && <div style={S.errorBanner}>{error}<button style={S.bannerClose} onClick={() => setError("")}>×</button></div>}
      {status && <div style={S.statusBanner}>{status}<button style={S.bannerClose} onClick={() => setStatusMsg("")}>×</button></div>}

      {/* ActivityWatch connection strip */}
      <ConnectionStrip
        awStatus={awStatus}
        onCheck={checkAW}
        onOpenSetup={() => setShowSetup(true)}
      />

      {/* Computers reporting into the audit */}
      <DevicesStrip
        devices={devices}
        awHostname={awStatus.hostname}
        onAdd={() => setShowSetup(true)}
      />

      {/* Session controls / start day */}
      {!session ? (
        <div style={S.startCard}>
          <div style={S.startCardTitle}>{fmtDateLong(day)} — no audit yet</div>
          <div style={S.startCardDesc}>
            Start the day to begin tracking. {awStatus.connected
              ? "ActivityWatch is connected — passive tracking is on."
              : "ActivityWatch isn't connected. You can still log manually with interval check-ins."}
          </div>
          <div style={S.startCardRow}>
            <label style={S.controlLabel}>Target hours</label>
            <input type="number" min="1" max="16" step="0.5" value={targetHours}
                   onChange={e => setTargetHours(Number(e.target.value) || 8)}
                   style={S.numInput} />
            <label style={S.controlLabel}>Manual ping interval</label>
            <select value={intervalMin} onChange={e => setIntervalMin(Number(e.target.value))} style={S.select}>
              <option value={15}>Every 15 min</option>
              <option value={30}>Every 30 min</option>
              <option value={45}>Every 45 min</option>
              <option value={60}>Every 60 min</option>
              <option value={90}>Every 90 min</option>
              <option value={120}>Every 2 hr</option>
            </select>
            <button onClick={startDay} disabled={!!busy} style={S.btnPrimary}>
              {busy === "starting" ? "Starting…" : "Start Day"}
            </button>
          </div>
        </div>
      ) : (
        <SessionToolbar
          session={session}
          awConnected={!!awStatus.connected}
          pingsEnabled={pingsEnabled}
          onTogglePings={() => setPingsEnabled(p => !p)}
          nextPingIn={nextPingIn}
          onSync={syncFromAW}
          onLogNow={openManualLog}
          onAudit={runAudit}
          onImportDrives={() => setShowImport(true)}
          busy={busy}
          eventsCount={events.length}
          slotsCount={slots.length}
          blocksCount={blocks.length}
          drivesCount={drives.length}
          lastAutoSyncAt={lastAutoSyncAt}
          isToday={day === todayLocal()}
        />
      )}

      {/* Timeline / stacked bar */}
      {session && (
        <CategoryTotals
          totals={totals}
          totalLoggedMin={totalLoggedMin}
          categories={categories}
          targetHours={targetHours}
          colorFor={colorFor}
        />
      )}

      {/* R&D / SR&ED micro-journal — contemporaneous note for the CRA claim */}
      {session && (
        <RndJournalCard
          note={rndNote}
          rndMinutes={rndMinutes}
          busy={busy}
          onSave={saveRndNote}
          onOpenReport={() => navigate("/coach/rnd")}
        />
      )}

      {/* AI summary panel */}
      {session?.summary_json && (
        <CoachSummary
          summary={session.summary_json}
          blocks={blocks}
          colorFor={colorFor}
        />
      )}

      {/* 14-day trend + hire-ROI math */}
      {session && (
        <TrendPanel
          open={trendOpen}
          onToggle={() => setTrendOpen(o => !o)}
          sessions={recentSessions}
          categories={categories}
          colorFor={colorFor}
          loading={trendBusy}
          onReload={loadTrend}
        />
      )}

      {/* Backfill banner — surfaces days that never made it to the server */}
      {session && (missingDays.length > 0 || backfillProgress) && (
        <BackfillBanner
          missingDays={missingDays}
          progress={backfillProgress}
          awConnected={!!awStatus.connected}
          onBackfill={backfillMissingDays}
        />
      )}

      {/* Past audits — collapsible per-day history */}
      {session && (
        <PastAuditsList
          sessions={recentSessions}
          currentDay={day}
          colorFor={colorFor}
          onPickDay={(d) => setDay(d)}
          onLoad={loadTrend}
          loading={trendBusy}
        />
      )}

      {/* Slots (manual check-ins) — only show when there are any */}
      {session && slots.length > 0 && (
        <SlotsList slots={slots} categories={categories} />
      )}

      {/* Drives — MileageIQ import or future GPS */}
      {session && drives.length > 0 && (
        <DrivesList
          drives={drives}
          onChange={() => loadDrives(session.id)}
        />
      )}

      {/* Modals */}
      {showSetup && (
        <SetupWizard
          awStatus={awStatus}
          onClose={() => setShowSetup(false)}
          onCheck={checkAW}
        />
      )}
      {showImport && (
        <ImportDrivesModal
          userEmail={userEmail}
          onClose={() => setShowImport(false)}
          onDone={async (stats) => {
            setShowImport(false);
            setStatusMsg(`Imported ${stats.inserted} drive${stats.inserted === 1 ? "" : "s"} across ${stats.days_touched} day${stats.days_touched === 1 ? "" : "s"}. Auto-matched ${stats.auto_visit + stats.auto_address}, unmatched ${stats.unmatched}.`);
            if (session) await loadDrives(session.id);
            await loadTrend();
            await reloadSession();
          }}
        />
      )}
      {showSettings && (
        <SettingsPanel
          categories={categories}
          userEmail={userEmail}
          onClose={() => setShowSettings(false)}
          onReload={async () => {
            const c = await apiFetch(`${API}?action=list-categories&user=${encodeURIComponent(userEmail)}`).then(r => r.json());
            if (c.ok) setCategories(c.categories || []);
          }}
        />
      )}
      {slotModal && (
        <SlotModal
          slot={slotModal}
          categories={categories}
          onClose={() => setSlotModal(null)}
          onSave={async (payload) => {
            await upsertSlot({ session_id: session.id, ...slotModal, ...payload });
            setSlotModal(null);
          }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ConnectionStrip — AW connected / not connected indicator
// ═══════════════════════════════════════════════════════════════════════════
function ConnectionStrip({ awStatus, onCheck, onOpenSetup }) {
  const connected = !!awStatus.connected;
  return (
    <div style={{ ...S.connStrip, borderColor: connected ? "rgba(34,197,94,0.3)" : "rgba(245,158,11,0.3)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{
          width: 10, height: 10, borderRadius: "50%",
          background: connected ? "#22c55e" : "#f59e0b",
          boxShadow: `0 0 8px ${connected ? "#22c55e" : "#f59e0b"}`,
        }} />
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#e8eaf0" }}>
            {connected ? "ActivityWatch connected" : "ActivityWatch not detected"}
          </div>
          <div style={{ fontSize: 11, color: "#8892a4" }}>
            {connected
              ? `Host: ${awStatus.hostname || "unknown"}${awStatus.version ? ` · v${awStatus.version}` : ""}`
              : "Manual interval check-ins still work, but passive tracking is off."}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={onCheck} style={S.btnSm}>Re-check</button>
        {!connected && <button onClick={onOpenSetup} style={S.btnPrimarySm}>Set up</button>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DevicesStrip — every computer that has synced AW data in the last 7 days
// ═══════════════════════════════════════════════════════════════════════════
function DevicesStrip({ devices, awHostname, onAdd }) {
  if (devices === null) return null; // still loading — don't flash an empty card
  const FRESH_MS = 26 * 60 * 60 * 1000; // synced within ~a day = healthy
  return (
    <div style={{ ...S.connStrip, borderColor: "rgba(255,255,255,0.08)", flexWrap: "wrap" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#8892a4", textTransform: "uppercase", letterSpacing: 0.5 }}>
          Computers
        </div>
        {devices.length === 0 && (
          <div style={{ fontSize: 12, color: "#8892a4" }}>No synced activity in the last 7 days yet.</div>
        )}
        {devices.map(d => {
          const fresh = d.last_event_at && (Date.now() - new Date(d.last_event_at).getTime()) < FRESH_MS;
          const isThis = awHostname && d.hostname === awHostname;
          return (
            <div key={d.hostname} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{
                width: 8, height: 8, borderRadius: "50%",
                background: fresh ? "#22c55e" : "#f59e0b",
              }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#e8eaf0" }}>
                  {d.hostname}
                  {isThis && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#a5ce3a", background: "rgba(165,206,58,0.12)", padding: "1px 6px", borderRadius: 4 }}>this computer</span>}
                </div>
                <div style={{ fontSize: 11, color: "#8892a4" }}>
                  last synced {fmtAgo(d.last_event_at)} · {d.days_seen}/7 days
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <a href={AUTOSYNC_BAT_URL} download style={S.btnSm} title="Hourly background sync for this computer — tracking works without this page open (Windows)">
          Auto-sync agent ↓
        </a>
        <button onClick={onAdd} style={S.btnPrimarySm}>Add a computer</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SessionToolbar — sync / log-now / audit / interval ping toggle
// ═══════════════════════════════════════════════════════════════════════════
function SessionToolbar({
  session, awConnected, pingsEnabled, onTogglePings, nextPingIn,
  onSync, onLogNow, onAudit, onImportDrives, busy,
  eventsCount, slotsCount, blocksCount, drivesCount,
  lastAutoSyncAt, isToday,
}) {
  const autoSyncLabel = useMemo(() => {
    if (!isToday || !awConnected || !lastAutoSyncAt) return null;
    const ms = Date.now() - new Date(lastAutoSyncAt).getTime();
    const min = Math.floor(ms / 60000);
    if (min < 1) return "auto-sync · just now";
    if (min === 1) return "auto-sync · 1 min ago";
    return `auto-sync · ${min} min ago`;
  }, [isToday, awConnected, lastAutoSyncAt]);
  return (
    <div style={S.toolbarCard}>
      <div style={S.toolbarLeft}>
        <div style={S.toolbarStat}>
          <div style={S.toolbarStatNum}>{eventsCount}</div>
          <div style={S.toolbarStatLabel}>AW events</div>
        </div>
        <div style={S.toolbarStat}>
          <div style={S.toolbarStatNum}>{slotsCount}</div>
          <div style={S.toolbarStatLabel}>Check-ins</div>
        </div>
        <div style={S.toolbarStat}>
          <div style={S.toolbarStatNum}>{drivesCount}</div>
          <div style={S.toolbarStatLabel}>Drives</div>
        </div>
        <div style={S.toolbarStat}>
          <div style={S.toolbarStatNum}>{blocksCount}</div>
          <div style={S.toolbarStatLabel}>Audited blocks</div>
        </div>
      </div>
      <div style={S.toolbarRight}>
        {autoSyncLabel && (
          <span style={{ fontSize: 11, color: "#8892a4", marginRight: 4 }} title="ElecBid is silently pulling new AW events every 5 min">
            {autoSyncLabel}
          </span>
        )}
        <button onClick={onLogNow} disabled={!!busy} style={S.btn}>Log check-in now</button>
        <button onClick={onImportDrives} disabled={!!busy} style={S.btn}>Import drives</button>
        <label style={S.pingToggle}>
          <input type="checkbox" checked={pingsEnabled} onChange={onTogglePings} />
          <span>Ping me {pingsEnabled && nextPingIn != null ? `(${Math.floor(nextPingIn / 60)}:${String(nextPingIn % 60).padStart(2, "0")})` : ""}</span>
        </label>
        {awConnected && (
          <button onClick={onSync} disabled={!!busy} style={S.btn}>
            {busy === "syncing" ? "Syncing…" : "Sync from AW"}
          </button>
        )}
        <button onClick={onAudit} disabled={!!busy || (eventsCount === 0 && slotsCount === 0)} style={S.btnPrimary}>
          {busy === "auditing" ? "Auditing…" : "Run AI audit"}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CategoryTotals — stacked horizontal bar + legend
// ═══════════════════════════════════════════════════════════════════════════
function CategoryTotals({ totals, totalLoggedMin, categories, targetHours, colorFor }) {
  const entries = Object.entries(totals).sort((a, b) => b[1] - a[1]);
  const targetMin = targetHours * 60;
  const cap = Math.max(totalLoggedMin, targetMin, 1);
  if (!entries.length) {
    return (
      <div style={S.card}>
        <div style={S.cardHeader}><div style={S.cardTitle}>Time by category</div></div>
        <div style={{ padding: 16, color: "#8892a4", fontSize: 13 }}>
          No data yet. {totalLoggedMin === 0 && "Click \"Run AI audit\" once you've synced events or logged check-ins."}
        </div>
      </div>
    );
  }
  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <div style={S.cardTitle}>Time by category</div>
        <div style={S.cardSubtitle}>
          {fmtMin(totalLoggedMin)} logged{totalLoggedMin < targetMin ? ` of ${targetHours}h target` : ""}
        </div>
      </div>
      <div style={{ padding: 16 }}>
        {/* Stacked bar */}
        <div style={S.stackedBar}>
          {entries.map(([cat, min]) => (
            <div key={cat} title={`${cat} — ${fmtMin(min)}`}
                 style={{
                   width: `${(min / cap) * 100}%`,
                   background: colorFor(cat),
                   height: "100%",
                 }} />
          ))}
        </div>
        {/* Legend */}
        <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8 }}>
          {entries.map(([cat, min]) => {
            const pct = totalLoggedMin > 0 ? min / totalLoggedMin : 0;
            const tgt = categories.find(c => c.name === cat)?.target_pct;
            return (
              <div key={cat} style={S.legendRow}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: colorFor(cat), flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: 13, color: "#e8eaf0" }}>{cat}</span>
                <span style={{ fontSize: 13, color: "#e8eaf0", fontWeight: 600 }}>{fmtMin(min)}</span>
                <span style={{ fontSize: 11, color: tgt != null && pct > tgt * 1.3 ? "#ef4444" : "#8892a4", minWidth: 36, textAlign: "right" }}>
                  {(pct * 100).toFixed(0)}%
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CoachSummary — kill / delegate / keep + hire signal + commentary
// ═══════════════════════════════════════════════════════════════════════════
function CoachSummary({ summary, blocks, colorFor }) {
  const hs = summary.hire_signal || {};
  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <div style={S.cardTitle}>Jai's coaching summary</div>
      </div>
      <div style={{ padding: 16 }}>
        {summary.headline && (
          <div style={S.headline}>{summary.headline}</div>
        )}

        <div style={S.kdkGrid}>
          <KdkColumn title="Kill" tint="#ef4444" items={summary.kill || []} />
          <KdkColumn title="Delegate" tint="#fbbf24" items={(summary.delegate || []).map(d =>
            typeof d === "string"
              ? d
              : `${d.task}${d.to_role ? ` → ${d.to_role}` : ""}${d.weekly_hours_estimate ? ` (~${d.weekly_hours_estimate} h/wk)` : ""}${d.framework ? ` · ${d.framework}` : ""}`
          )} />
          <KdkColumn title="Keep" tint="#22c55e" items={summary.keep || []} />
        </div>

        {hs.trigger && (
          <div style={S.hireCard}>
            <div style={{ fontSize: 13, color: "#fbbf24", fontWeight: 700, marginBottom: 6 }}>HIRE SIGNAL · {hs.confidence?.toUpperCase() || "MEDIUM"}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#e8eaf0" }}>
              {hs.role || "—"}
            </div>
            {hs.weekly_hours_recovered != null && (
              <div style={{ fontSize: 13, color: "#a5ce3a", marginTop: 4 }}>
                Could recover ~{hs.weekly_hours_recovered} hr/wk of owner time
              </div>
            )}
            {hs.framework && <div style={{ fontSize: 12, color: "#8892a4", marginTop: 6 }}>{hs.framework}</div>}
            {hs.rationale && <div style={{ fontSize: 13, color: "#e8eaf0", marginTop: 8, lineHeight: 1.5 }}>{hs.rationale}</div>}
          </div>
        )}

        {summary.coach_commentary && (
          <div style={S.commentary}>
            {summary.coach_commentary.split(/\n\n+/).map((p, i) => (
              <p key={i} style={{ margin: "0 0 10px 0", lineHeight: 1.55 }}>{p}</p>
            ))}
          </div>
        )}

        {blocks.length > 0 && (
          <details style={{ marginTop: 18 }}>
            <summary style={{ cursor: "pointer", fontSize: 13, color: "#8892a4" }}>
              Show audited blocks ({blocks.length})
            </summary>
            <div style={{ marginTop: 10 }}>
              {blocks.map(b => (
                <div key={b.id} style={S.blockRow}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 130 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: colorFor(b.category) }} />
                    <span style={{ fontSize: 11, color: "#8892a4" }}>
                      {fmtTimeShort(b.started_at)}–{fmtTimeShort(b.ended_at)}
                    </span>
                  </div>
                  <div style={{ flex: 1, fontSize: 13, color: "#e8eaf0" }}>
                    <div>
                      {b.activity || "—"}
                      {b.category === RND_CATEGORY && (
                        <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: "#a5b4fc", background: "rgba(129,140,248,0.15)", border: "1px solid rgba(129,140,248,0.4)", borderRadius: 4, padding: "1px 5px", textTransform: "uppercase", letterSpacing: "0.04em" }}>R&amp;D</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: "#8892a4" }}>{b.category}{b.delegatable ? " · delegatable" : ""}</div>
                    {b.rd_json?.uncertainty && (
                      <div style={{ fontSize: 11, color: "#a5b4fc", marginTop: 2, lineHeight: 1.4 }} title="Technological uncertainty (SR&ED)">
                        ⚗ {b.rd_json.uncertainty}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: "#e8eaf0", fontWeight: 600 }}>{fmtSecAsHM(b.duration_sec)}</div>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// RndJournalCard — the daily R&D / SR&ED micro-journal. The owner's own
// contemporaneous words about what technological problem they were cracking —
// the single most defensible piece of evidence in a CRA SR&ED claim. Saving
// re-runs the day's audit so the note feeds the AI's rd narratives.
// ═══════════════════════════════════════════════════════════════════════════
function RndJournalCard({ note, rndMinutes, busy, onSave, onOpenReport }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [projects, setProjects] = useState("");
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const recRef = useRef(null);
  const speechAvailable = !!getSR();

  // Hydrate the fields only when the saved note changes — never on rndMinutes,
  // so an auto-sync re-audit mid-edit can't clobber what the owner is typing.
  useEffect(() => {
    setText(note?.note_text || "");
    setProjects(note?.projects || "");
  }, [note]);

  // Auto-open the card when R&D was detected but no note exists yet.
  useEffect(() => {
    if (!note?.note_text && rndMinutes > 0) setOpen(true);
  }, [note, rndMinutes]);

  function startListening() {
    const SR = getSR();
    if (!SR) return;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-CA";
    let finalAcc = "";
    rec.onresult = (e) => {
      let interimStr = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalAcc += e.results[i][0].transcript + " ";
        else interimStr += e.results[i][0].transcript;
      }
      if (finalAcc) { setText(prev => (prev + " " + finalAcc).trim()); finalAcc = ""; }
      setInterim(interimStr);
    };
    rec.onend = () => { setListening(false); setInterim(""); };
    rec.onerror = () => { setListening(false); setInterim(""); };
    rec.start();
    recRef.current = rec;
    setListening(true);
  }
  function stopListening() {
    if (recRef.current) { try { recRef.current.stop(); } catch {} recRef.current = null; }
    setListening(false);
  }

  const hasNote = !!note?.note_text;
  const saving = busy === "saving-rnd" || busy === "auditing";

  return (
    <div style={{ ...S.card, borderColor: "rgba(129,140,248,0.3)" }}>
      <div style={{ ...S.cardHeader, cursor: "pointer" }} onClick={() => setOpen(o => !o)}>
        <div style={S.cardTitle}>
          <span style={{ color: "#818cf8" }}>R&amp;D journal</span>
          {rndMinutes > 0 && (
            <span style={{ marginLeft: 10, fontSize: 12, color: "#a5b4fc", fontWeight: 600 }}>
              {fmtMin(rndMinutes)} of R&amp;D detected today
            </span>
          )}
          {hasNote && <span style={{ marginLeft: 8, fontSize: 11, color: "#22c55e" }}>✓ noted</span>}
        </div>
        <div style={S.cardSubtitle}>{open ? "▾" : "▸"} {open ? "collapse" : "add a note"}</div>
      </div>
      {open && (
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 12, color: "#8892a4", marginBottom: 10, lineHeight: 1.55 }}>
            In your own words: <strong style={{ color: "#cbd5e1" }}>what were you trying to crack today, and what was technically uncertain?</strong> (e.g. testing whether Claude could reliably extract line-items from a supplier PDF, tuning the quoting prompt, a tricky Jobber↔QuickBooks sync.) This dated note is the strongest evidence for next year's SR&amp;ED claim.
          </div>
          <textarea
            value={text + (interim ? ` ${interim}` : "")}
            onChange={e => setText(e.target.value)}
            placeholder="What technological problem were you working on, and why wasn't the answer obvious?"
            style={S.textArea}
          />
          <div style={{ display: "flex", gap: 10, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
            {speechAvailable && (
              listening
                ? <button onClick={stopListening} style={S.btnRecording}>● Stop recording</button>
                : <button onClick={startListening} style={S.btnSm}>🎤 Speak</button>
            )}
            <input
              value={projects}
              onChange={e => setProjects(e.target.value)}
              placeholder="Project(s) — e.g. AI Quoting Engine"
              style={{ ...S.txtInput, flex: 1, minWidth: 200 }}
            />
            <button
              onClick={() => onSave({ note_text: text.trim(), projects: projects.trim() || null })}
              disabled={saving || !text.trim()}
              style={S.btnPrimary}
            >
              {busy === "auditing" ? "Re-auditing…" : busy === "saving-rnd" ? "Saving…" : "Save note"}
            </button>
            <button onClick={onOpenReport} style={S.btnSm}>Open SR&amp;ED report →</button>
          </div>
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 10, lineHeight: 1.5 }}>
            Saving re-runs today's audit so the note shapes the R&amp;D write-up. Tip: keep it factual — the uncertainty, what you tried, what you learned.
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TrendPanel — 14-day stacked bars + sustained-pattern hire-ROI math
// ═══════════════════════════════════════════════════════════════════════════
function TrendPanel({ open, onToggle, sessions, categories, colorFor, loading, onReload }) {
  // Aggregate per category across the period (uses cached totals_json from each
  // session, so this is one cheap query — no per-block fetch needed).
  const { perCategoryMin, dayBars, periodDays, totalLogged } = useMemo(() => {
    const perCat = {};
    const bars = [];
    const audited = sessions.filter(s => s.totals_json && Object.keys(s.totals_json || {}).length);
    for (const s of audited) {
      let dayTotal = 0;
      const slices = [];
      for (const [k, v] of Object.entries(s.totals_json || {})) {
        const mins = Number(v) || 0;
        perCat[k] = (perCat[k] || 0) + mins;
        dayTotal += mins;
        slices.push({ cat: k, mins });
      }
      bars.push({ day: s.day, total: dayTotal, slices, target_hours: Number(s.target_hours) || 8 });
    }
    bars.sort((a, b) => a.day.localeCompare(b.day));
    return {
      perCategoryMin: perCat,
      dayBars: bars,
      periodDays: audited.length || 0,
      totalLogged: Object.values(perCat).reduce((s, v) => s + v, 0),
    };
  }, [sessions]);

  // ROI: for each category with hourly_value set, compute weekly $-recoverable
  // when actual time exceeds target by a meaningful margin.
  const roiRows = useMemo(() => {
    if (periodDays < 2) return [];
    const rows = [];
    for (const c of categories) {
      const actualMin = perCategoryMin[c.name] || 0;
      if (actualMin <= 0) continue;
      const avgPerDay = actualMin / periodDays;
      // target_pct stored as decimal (0.15 = 15%). Assume an 8h working day.
      const targetMin = c.target_pct != null ? Number(c.target_pct) * 8 * 60 : null;
      let overrunPerDay = null;
      if (targetMin != null) overrunPerDay = avgPerDay - targetMin;
      const hourly = Number(c.hourly_value) || 0;
      if (hourly <= 0) continue;
      // Show row even without target, if actual is meaningful (>= 30 min/day avg)
      if (avgPerDay < 30 && (overrunPerDay == null || overrunPerDay <= 0)) continue;
      const weeklyHours = (overrunPerDay && overrunPerDay > 0 ? overrunPerDay : avgPerDay) * 5 / 60;
      const weeklyDollars = weeklyHours * hourly;
      rows.push({
        category: c.name,
        avgPerDay,
        targetMin,
        overrunPerDay,
        weeklyHours,
        weeklyDollars,
        hourly,
      });
    }
    rows.sort((a, b) => b.weeklyDollars - a.weeklyDollars);
    return rows;
  }, [categories, perCategoryMin, periodDays]);

  const maxBar = useMemo(() => Math.max(...dayBars.map(b => b.total), 60), [dayBars]);

  return (
    <div style={S.card}>
      <div style={{ ...S.cardHeader, cursor: "pointer" }} onClick={onToggle}>
        <div style={S.cardTitle}>14-day trend & hire-ROI {periodDays > 0 ? `· ${periodDays} audited day${periodDays === 1 ? "" : "s"}` : ""}</div>
        <div style={S.cardSubtitle}>{open ? "▾" : "▸"} click to {open ? "collapse" : "expand"}</div>
      </div>
      {open && (
        <div style={{ padding: 16 }}>
          {loading && <div style={{ color: "#8892a4", fontSize: 13 }}>Loading…</div>}
          {!loading && periodDays === 0 && (
            <div style={{ color: "#8892a4", fontSize: 13 }}>
              No audited days yet. Run "Run AI audit" on one or more days to see trends.
              <button onClick={onReload} style={{ ...S.btnSm, marginLeft: 10 }}>Refresh</button>
            </div>
          )}

          {/* Per-day stacked bars */}
          {dayBars.length > 0 && (
            <>
              <div style={{ marginBottom: 12, fontSize: 12, color: "#8892a4" }}>
                {fmtMin(totalLogged)} total across {periodDays} day{periodDays === 1 ? "" : "s"} · avg {fmtMin(Math.round(totalLogged / periodDays))}/day
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {dayBars.map(bar => {
                  const [y, m, d] = bar.day.split("-").map(Number);
                  const label = new Date(y, m - 1, d).toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" });
                  return (
                    <div key={bar.day} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 110, fontSize: 12, color: "#8892a4", flexShrink: 0 }}>{label}</div>
                      <div style={{ flex: 1, display: "flex", height: 14, borderRadius: 7, overflow: "hidden", background: "rgba(255,255,255,0.04)" }}>
                        {bar.slices.map((s, i) => (
                          <div key={i}
                               title={`${s.cat} — ${fmtMin(s.mins)}`}
                               style={{
                                 width: `${(s.mins / maxBar) * 100}%`,
                                 background: colorFor(s.cat),
                                 height: "100%",
                               }} />
                        ))}
                      </div>
                      <div style={{ width: 56, textAlign: "right", fontSize: 12, color: "#e8eaf0", fontWeight: 600 }}>{fmtMin(bar.total)}</div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ROI panel */}
          {roiRows.length > 0 && (
            <div style={{ marginTop: 22, padding: 14, background: "rgba(251,191,36,0.05)", border: "1px solid rgba(251,191,36,0.2)", borderRadius: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#fbbf24", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Recoverable owner-time
              </div>
              <div style={{ fontSize: 12, color: "#8892a4", marginBottom: 10, lineHeight: 1.5 }}>
                Estimated weekly $ tied up in each category, based on your set $/hr value.
                Categories above their target are highlighted as overflow.
              </div>
              {roiRows.map(r => (
                <div key={r.category} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                  <div style={{ flex: 1, fontSize: 13, color: "#e8eaf0" }}>
                    <div style={{ fontWeight: 600 }}>{r.category}</div>
                    <div style={{ fontSize: 11, color: "#8892a4" }}>
                      avg {fmtMin(Math.round(r.avgPerDay))}/day
                      {r.targetMin != null && r.overrunPerDay > 0 && (
                        <> · <span style={{ color: "#fbbf24" }}>{fmtMin(Math.round(r.overrunPerDay))}/day over target</span></>
                      )}
                      {" · @ "}${r.hourly}/hr
                    </div>
                  </div>
                  <div style={{ textAlign: "right", minWidth: 130 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#a5ce3a" }}>
                      ${Math.round(r.weeklyDollars).toLocaleString()}/wk
                    </div>
                    <div style={{ fontSize: 11, color: "#8892a4" }}>{r.weeklyHours.toFixed(1)} hr/wk recoverable</div>
                  </div>
                </div>
              ))}
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.06)", fontSize: 11, color: "#8892a4", lineHeight: 1.5 }}>
                Math: avg minutes/day × 5 working days × $/hr. If a category has a target %, the overflow above target is used instead.
                Tune $/hr per category in Settings.
              </div>
            </div>
          )}

          {periodDays > 0 && roiRows.length === 0 && (
            <div style={{ marginTop: 14, fontSize: 12, color: "#8892a4" }}>
              No hire-ROI signal yet. Set <code style={S.code}>$/hr</code> on a category in Settings to enable the math.
            </div>
          )}

          {periodDays > 0 && (
            <button onClick={onReload} style={{ ...S.btnSm, marginTop: 14 }}>Refresh trend</button>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// BackfillBanner — shows missing days between sessions and offers a one-click
// catch-up. Only shows when AW is connected (it's the only way to get those
// events). Progress object is { total, done, current, phase }.
// ═══════════════════════════════════════════════════════════════════════════
function BackfillBanner({ missingDays, progress, awConnected, onBackfill }) {
  const running = progress && progress.phase !== "done";
  const finished = progress && progress.phase === "done";
  const summary = useMemo(() => {
    if (!missingDays.length) return null;
    const first = missingDays[0];
    const last = missingDays[missingDays.length - 1];
    return first === last ? first : `${first} → ${last}`;
  }, [missingDays]);
  return (
    <div style={{
      ...S.card,
      marginBottom: 16,
      borderColor: running ? "rgba(96,165,250,0.4)" : "rgba(251,191,36,0.35)",
      background: running ? "rgba(96,165,250,0.06)" : "rgba(251,191,36,0.05)",
    }}>
      <div style={{ padding: 14, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          {!progress && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#e8eaf0" }}>
                {missingDays.length} day{missingDays.length === 1 ? "" : "s"} never made it to the server
              </div>
              <div style={{ fontSize: 12, color: "#a5b4cf", marginTop: 4 }}>
                {summary} — the tab wasn't open those days, so AW events never uploaded. Backfill pulls them from your local ActivityWatch and runs each audit.
              </div>
            </>
          )}
          {running && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#e8eaf0" }}>
                Backfilling… {progress.done} / {progress.total}
              </div>
              <div style={{ fontSize: 12, color: "#a5b4cf", marginTop: 4 }}>
                {progress.phase === "syncing" ? "Pulling AW events for " : "Auditing "}
                <code style={{ background: "rgba(0,0,0,0.25)", padding: "1px 6px", borderRadius: 3 }}>{progress.current}</code>
              </div>
            </>
          )}
          {finished && (
            <div style={{ fontSize: 13, fontWeight: 700, color: "#22c55e" }}>
              Backfill complete — {progress.total} day{progress.total === 1 ? "" : "s"} processed.
            </div>
          )}
        </div>
        {!progress && (
          <button
            onClick={onBackfill}
            disabled={!awConnected}
            title={!awConnected ? "ActivityWatch must be connected to pull the missing events" : ""}
            style={{
              ...S.btnPrimary,
              opacity: awConnected ? 1 : 0.5,
              cursor: awConnected ? "pointer" : "not-allowed",
            }}
          >
            Backfill {missingDays.length} day{missingDays.length === 1 ? "" : "s"}
          </button>
        )}
        {running && (
          <div style={{
            width: 140, height: 6, borderRadius: 3, background: "rgba(255,255,255,0.06)", overflow: "hidden",
          }}>
            <div style={{
              width: `${(progress.done / Math.max(progress.total, 1)) * 100}%`,
              height: "100%", background: "#60a5fa", transition: "width 0.3s",
            }} />
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PastAuditsList — collapsible per-day history of audited sessions
// ═══════════════════════════════════════════════════════════════════════════
function PastAuditsList({ sessions, currentDay, colorFor, onPickDay, onLoad, loading }) {
  const [expanded, setExpanded] = useState({});
  const [open, setOpen] = useState(false);

  // Show all sessions except the one currently displayed in the main view
  const pastSessions = useMemo(
    () => sessions.filter(s => s.day !== currentDay).sort((a, b) => b.day.localeCompare(a.day)),
    [sessions, currentDay]
  );

  useEffect(() => {
    if (open && sessions.length === 0 && !loading) onLoad();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={S.card}>
      <div style={{ ...S.cardHeader, cursor: "pointer" }} onClick={() => setOpen(o => !o)}>
        <div style={S.cardTitle}>
          Past audits{pastSessions.length > 0 ? ` · ${pastSessions.length}` : ""}
        </div>
        <div style={S.cardSubtitle}>{open ? "▾" : "▸"} click to {open ? "collapse" : "expand"}</div>
      </div>

      {open && (
        <div style={{ padding: 16 }}>
          {loading && <div style={{ fontSize: 13, color: "#8892a4" }}>Loading…</div>}
          {!loading && pastSessions.length === 0 && (
            <div style={{ fontSize: 13, color: "#8892a4" }}>
              No past audits yet. Once you run more day-audits they'll appear here, collapsed by day.
            </div>
          )}
          {pastSessions.map(s => {
            const summary = s.summary_json || null;
            const totals = s.totals_json || {};
            const totalMin = Object.values(totals).reduce((a, v) => a + (Number(v) || 0), 0);
            const isOpen = !!expanded[s.day];
            const [y, m, d] = s.day.split("-").map(Number);
            const label = new Date(y, m - 1, d).toLocaleDateString("en-CA", {
              weekday: "short", month: "short", day: "numeric", year: "numeric",
            });
            // Mini-bar for this day
            const entries = Object.entries(totals).sort((a, b) => b[1] - a[1]);
            const maxBar = Math.max(totalMin, 1);

            return (
              <div key={s.day} style={S.pastDay}>
                <div
                  onClick={() => setExpanded(e => ({ ...e, [s.day]: !e[s.day] }))}
                  style={S.pastDayHeader}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#e8eaf0" }}>
                      <span style={{ color: "#8892a4", fontWeight: 600, marginRight: 8 }}>{isOpen ? "▾" : "▸"}</span>
                      {label}
                    </div>
                    {summary?.headline && (
                      <div style={{ fontSize: 12, color: "#a5ce3a", marginTop: 4, lineHeight: 1.4 }}>
                        {summary.headline}
                      </div>
                    )}
                  </div>
                  <div style={{ minWidth: 80, textAlign: "right", fontSize: 12 }}>
                    <div style={{ color: "#e8eaf0", fontWeight: 700 }}>{fmtMin(totalMin)}</div>
                    {summary?.hire_signal?.trigger && (
                      <div style={{ color: "#fbbf24", fontSize: 10, fontWeight: 700, marginTop: 2 }}>HIRE ⚠</div>
                    )}
                  </div>
                </div>

                {/* Mini stacked bar — visible even when collapsed */}
                {entries.length > 0 && (
                  <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", background: "rgba(255,255,255,0.04)", marginTop: 8 }}>
                    {entries.map(([cat, min]) => (
                      <div key={cat}
                           title={`${cat} — ${fmtMin(min)}`}
                           style={{ width: `${(min / maxBar) * 100}%`, background: colorFor(cat), height: "100%" }} />
                    ))}
                  </div>
                )}

                {/* Expanded body */}
                {isOpen && summary && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                    <div style={S.kdkGrid}>
                      <KdkColumn title="Kill" tint="#ef4444" items={summary.kill || []} />
                      <KdkColumn title="Delegate" tint="#fbbf24" items={(summary.delegate || []).map(d =>
                        typeof d === "string"
                          ? d
                          : `${d.task}${d.to_role ? ` → ${d.to_role}` : ""}${d.weekly_hours_estimate ? ` (~${d.weekly_hours_estimate} h/wk)` : ""}`
                      )} />
                      <KdkColumn title="Keep" tint="#22c55e" items={summary.keep || []} />
                    </div>
                    {summary.hire_signal?.trigger && (
                      <div style={{ ...S.hireCard, marginTop: 12 }}>
                        <div style={{ fontSize: 11, color: "#fbbf24", fontWeight: 700, marginBottom: 4 }}>
                          HIRE SIGNAL · {(summary.hire_signal.confidence || "medium").toUpperCase()}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#e8eaf0" }}>
                          {summary.hire_signal.role || "—"}
                        </div>
                        {summary.hire_signal.rationale && (
                          <div style={{ fontSize: 12, color: "#cbd5e1", marginTop: 6, lineHeight: 1.4 }}>
                            {summary.hire_signal.rationale}
                          </div>
                        )}
                      </div>
                    )}
                    {summary.coach_commentary && (
                      <div style={{ ...S.commentary, marginTop: 12 }}>
                        {summary.coach_commentary.split(/\n\n+/).slice(0, 2).map((p, i) => (
                          <p key={i} style={{ margin: "0 0 8px 0", lineHeight: 1.5, fontSize: 12 }}>{p}</p>
                        ))}
                      </div>
                    )}
                    <button onClick={() => onPickDay(s.day)} style={{ ...S.btnSm, marginTop: 10 }}>
                      Open this day →
                    </button>
                  </div>
                )}

                {/* No-summary fallback */}
                {isOpen && !summary && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.05)", fontSize: 12, color: "#8892a4" }}>
                    Session exists but hasn't been audited yet.
                    <button onClick={() => onPickDay(s.day)} style={{ ...S.btnSm, marginLeft: 10 }}>
                      Open this day →
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          <button onClick={onLoad} style={{ ...S.btnSm, marginTop: 14 }}>Refresh history</button>
        </div>
      )}
    </div>
  );
}

function KdkColumn({ title, tint, items }) {
  return (
    <div style={S.kdkCol}>
      <div style={{ ...S.kdkTitle, color: tint }}>{title}</div>
      {(!items || items.length === 0) && (
        <div style={{ fontSize: 12, color: "#8892a4", fontStyle: "italic" }}>nothing flagged</div>
      )}
      {items.map((it, i) => (
        <div key={i} style={S.kdkItem}>{it}</div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SlotsList — manual interval check-ins for the day
// ═══════════════════════════════════════════════════════════════════════════
function SlotsList({ slots, categories }) {
  return (
    <div style={S.card}>
      <div style={S.cardHeader}><div style={S.cardTitle}>Manual check-ins ({slots.length})</div></div>
      <div style={{ padding: 16 }}>
        {slots.map(s => (
          <div key={s.id} style={S.blockRow}>
            <div style={{ minWidth: 130, fontSize: 11, color: "#8892a4" }}>
              {fmtTimeShort(s.slot_start)}–{fmtTimeShort(s.slot_end)}
            </div>
            <div style={{ flex: 1, fontSize: 13, color: "#e8eaf0" }}>
              {s.entry_text || <span style={{ color: "#8892a4", fontStyle: "italic" }}>(skipped)</span>}
              {s.category && <div style={{ fontSize: 11, color: "#8892a4" }}>{s.category}</div>}
            </div>
            <div style={{ fontSize: 10, color: "#8892a4" }}>{s.entry_source}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DrivesList — drives for the current day (MileageIQ-imported or GPS)
// ═══════════════════════════════════════════════════════════════════════════
function DrivesList({ drives, onChange }) {
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState(null);

  async function saveLabel(id, label) {
    setSaving(id);
    try {
      await apiFetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "link-drive", id, matched_label: label }),
      });
      await onChange();
      setEditing(e => ({ ...e, [id]: undefined }));
    } finally { setSaving(null); }
  }
  async function removeDrive(id) {
    if (!window.confirm("Remove this drive?")) return;
    setSaving(id);
    try {
      await apiFetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "delete-drive", id }),
      });
      await onChange();
    } finally { setSaving(null); }
  }

  const sourcePill = (src) => {
    const m = {
      "auto-visit":   { bg: "rgba(34,197,94,0.12)",  fg: "#22c55e", label: "auto · visit" },
      "auto-address": { bg: "rgba(165,206,58,0.12)", fg: "#a5ce3a", label: "auto · address" },
      "manual":       { bg: "rgba(96,165,250,0.12)", fg: "#60a5fa", label: "manual" },
      "unmatched":    { bg: "rgba(245,158,11,0.12)", fg: "#f59e0b", label: "unmatched" },
    }[src] || { bg: "rgba(255,255,255,0.05)", fg: "#8892a4", label: src };
    return (
      <span style={{ background: m.bg, color: m.fg, fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>{m.label}</span>
    );
  };

  return (
    <div style={S.card}>
      <div style={S.cardHeader}><div style={S.cardTitle}>Drives ({drives.length})</div></div>
      <div style={{ padding: 16 }}>
        {drives.map(d => {
          const isEditing = editing[d.id] !== undefined;
          const label = isEditing ? editing[d.id] : (d.matched_label || d.end_address || "—");
          return (
            <div key={d.id} style={S.driveRow}>
              <div style={{ minWidth: 140, fontSize: 11, color: "#8892a4" }}>
                {fmtTimeShort(d.started_at)}–{fmtTimeShort(d.ended_at)}
                {d.distance_km != null && (
                  <div style={{ marginTop: 2 }}>{Number(d.distance_km).toFixed(1)} km</div>
                )}
              </div>
              <div style={{ flex: 1, fontSize: 13, color: "#e8eaf0", minWidth: 0 }}>
                {isEditing ? (
                  <input
                    value={label}
                    onChange={e => setEditing(prev => ({ ...prev, [d.id]: e.target.value }))}
                    onKeyDown={e => { if (e.key === "Enter") saveLabel(d.id, label); }}
                    autoFocus
                    style={{ ...S.txtInput, width: "100%" }}
                  />
                ) : (
                  <div
                    onClick={() => setEditing(prev => ({ ...prev, [d.id]: d.matched_label || "" }))}
                    style={{ cursor: "pointer" }}
                    title="Click to edit"
                  >
                    {label}
                  </div>
                )}
                <div style={{ fontSize: 11, color: "#8892a4", marginTop: 4, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  {sourcePill(d.match_source)}
                  {d.matched_job_number && <span>Job #{d.matched_job_number}</span>}
                  {d.purpose && <span style={{ color: "#cbd5e1" }}>{d.purpose}</span>}
                  {d.end_address && d.end_address !== d.matched_label && (
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 320 }} title={d.end_address}>
                      → {d.end_address}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {isEditing ? (
                  <>
                    <button onClick={() => saveLabel(d.id, label)} disabled={saving === d.id} style={S.btnPrimarySm}>Save</button>
                    <button onClick={() => setEditing(prev => ({ ...prev, [d.id]: undefined }))} style={S.btnSm}>Cancel</button>
                  </>
                ) : (
                  <button onClick={() => removeDrive(d.id)} disabled={saving === d.id} style={S.btnSmDanger}>×</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ImportDrivesModal — paste MileageIQ / TripLog CSV
// ═══════════════════════════════════════════════════════════════════════════
function ImportDrivesModal({ userEmail, onClose, onDone }) {
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [source, setSource] = useState("mileiq");
  const [distanceUnit, setDistanceUnit] = useState("miles");

  async function doImport() {
    setBusy(true); setError("");
    try {
      const r = await apiFetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "import-drives",
          user_email: userEmail,
          csv,
          source,
          distance_unit: distanceUnit,
        }),
      }).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Import failed");
      onDone(r.stats);
    } catch (e) {
      setError(e.message);
    } finally { setBusy(false); }
  }

  // Quick stats from textarea (rows minus header)
  const lineCount = useMemo(() => {
    const lines = csv.split("\n").filter(l => l.trim());
    return Math.max(0, lines.length - 1);
  }, [csv]);

  return (
    <div style={S.modalBackdrop} onClick={onClose}>
      <div style={{ ...S.modal, maxWidth: 760 }} onClick={e => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Import drives</div>
          <button onClick={onClose} style={S.modalClose}>×</button>
        </div>
        <div style={S.modalBody}>
          <div style={{ fontSize: 12, color: "#8892a4", marginBottom: 10, lineHeight: 1.55 }}>
            Paste a CSV from MileageIQ (Web → Export → Download). Drives auto-sort into each day,
            and we try to match them to your Jobber visits + clients by address. You can paste any
            date range — even months at a time. Drives are de-duplicated by drive-ID so re-imports
            are safe.
          </div>

          <div style={{ display: "flex", gap: 10, marginBottom: 10, alignItems: "center", flexWrap: "wrap" }}>
            <label style={S.controlLabel}>Source</label>
            <select value={source} onChange={e => setSource(e.target.value)} style={S.select}>
              <option value="mileiq">MileageIQ</option>
              <option value="triplog">TripLog</option>
            </select>
            <label style={S.controlLabel}>Distance unit</label>
            <select value={distanceUnit} onChange={e => setDistanceUnit(e.target.value)} style={S.select}>
              <option value="miles">Miles</option>
              <option value="km">Kilometers</option>
            </select>
            {lineCount > 0 && (
              <span style={{ fontSize: 12, color: "#a5ce3a", marginLeft: "auto" }}>
                {lineCount} drive{lineCount === 1 ? "" : "s"} detected
              </span>
            )}
          </div>

          <textarea
            value={csv}
            onChange={e => setCsv(e.target.value)}
            placeholder='Paste CSV here. First line should be column headers (e.g. "Start Date","Start Time","End Date","End Time","Start Location","End Location","Distance","Purpose")'
            style={{ ...S.textArea, minHeight: 260, fontFamily: "monospace", fontSize: 12 }}
            autoFocus
          />

          {error && (
            <div style={{ marginTop: 10, padding: "8px 10px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 6, color: "#fca5a5", fontSize: 12 }}>
              {error}
            </div>
          )}
        </div>
        <div style={S.modalFooter}>
          <button onClick={onClose} style={S.btnSubtle}>Cancel</button>
          <button onClick={doImport} disabled={!csv.trim() || busy} style={S.btnPrimary}>
            {busy ? "Importing…" : "Import drives"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SetupWizard — guide user through installing ActivityWatch + CORS
// ═══════════════════════════════════════════════════════════════════════════
function SetupWizard({ awStatus, onClose, onCheck }) {
  const detected = detectOS();
  const [osTab, setOsTab] = useState(detected);
  return (
    <div style={S.modalBackdrop} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Set up time tracking</div>
          <button onClick={onClose} style={S.modalClose}>×</button>
        </div>
        <div style={S.modalBody}>
          <p style={S.wizardP}>
            ActivityWatch is a free, open-source app that runs on your computer and quietly tracks which window
            you're in. Data stays on your machine — ElecBid pulls the summary only when you click <strong>Sync</strong>.
          </p>

          {/* OS tabs */}
          <div style={S.osTabs}>
            {[
              { id: "windows", label: "Windows" },
              { id: "mac", label: "Mac" },
              { id: "android", label: "Android" },
              { id: "ios", label: "iPhone / iPad" },
              { id: "linux", label: "Linux" },
            ].map(t => (
              <button key={t.id}
                      onClick={() => setOsTab(t.id)}
                      style={{
                        ...S.osTabBtn,
                        ...(osTab === t.id ? S.osTabBtnActive : {}),
                      }}>
                {t.label}{detected === t.id ? " ·" : ""}
              </button>
            ))}
          </div>

          {osTab === "windows" && <WindowsSteps awStatus={awStatus} onCheck={onCheck} />}
          {osTab === "mac" && <MacSteps awStatus={awStatus} onCheck={onCheck} />}
          {osTab === "android" && <AndroidSteps awStatus={awStatus} onCheck={onCheck} />}
          {osTab === "ios" && <IosSteps onClose={onClose} />}
          {osTab === "linux" && <LinuxSteps awStatus={awStatus} onCheck={onCheck} />}
        </div>
        <div style={S.modalFooter}>
          <button onClick={onClose} style={S.btnPrimary}>Done</button>
        </div>
      </div>
    </div>
  );
}

function ConnectionFootStep({ awStatus, onCheck }) {
  return (
    <div style={S.wizardStep}>
      <div style={S.wizardStepNum}>✓</div>
      <div style={{ flex: 1 }}>
        <div style={S.wizardStepTitle}>Verify the connection</div>
        <div style={S.wizardStepBody}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span style={{
              width: 10, height: 10, borderRadius: "50%",
              background: awStatus.connected ? "#22c55e" : "#f59e0b",
            }} />
            <span style={{ fontSize: 13, color: "#e8eaf0" }}>
              {awStatus.connected ? "Connected — you're good to go." : "Not detected yet."}
            </span>
          </div>
          <button onClick={onCheck} style={S.btnSm}>Re-check now</button>
          <div style={{ fontSize: 11, color: "#8892a4", marginTop: 6 }}>
            This page auto-rechecks every 5 sec while open.
          </div>
        </div>
      </div>
    </div>
  );
}

function WindowsSteps({ awStatus, onCheck }) {
  return (
    <>
      <WStep n={1} title="Download ActivityWatch">
        <a href="https://activitywatch.net/downloads/" target="_blank" rel="noopener noreferrer" style={S.btnPrimarySm}>
          Open downloads page
        </a>
        <div style={S.wizardHint}>Grab the Windows installer (.exe) and run it. Accept the defaults.</div>
      </WStep>
      <WStep n={2} title="Install the browser extension (Chrome / Edge)">
        <a href="https://chrome.google.com/webstore/detail/activitywatch-web-watcher/nglaklhklhcoonedhgnpgddginnjdadi"
           target="_blank" rel="noopener noreferrer" style={S.btnSm}>Open Chrome Web Store</a>
        <div style={S.wizardHint}>Optional but recommended — tracks which website tab you're actively on.</div>
      </WStep>
      <WStep n={3} title="Allow ElecBid to read your activity (one-time)">
        <a href={SETUP_BAT_URL} download style={S.btnPrimarySm}>Download setup .bat</a>
        <a href={SETUP_PS1_URL} download style={{ ...S.btnSm, marginLeft: 8 }}>or .ps1</a>
        <div style={S.wizardHint}>
          Double-click the .bat file once after installing ActivityWatch. It writes a backup of your config first,
          adds <code style={S.code}>elecbid.vercel.app</code> to AW's allowlist, restarts the tracker, and probes
          localhost:5600 to confirm it came back online.
        </div>
      </WStep>
      <WStep n={4} title="Turn on background auto-sync (recommended)">
        <a href={AUTOSYNC_BAT_URL} download style={S.btnPrimarySm}>Download auto-sync .bat</a>
        <div style={S.wizardHint}>
          Double-click once. Installs an hourly background task that pushes this computer's activity to
          ElecBid — tracking and the nightly audit keep working even when this page isn't open. It asks
          which ElecBid email to file under (just press Enter for the default), and restarts ActivityWatch
          if it isn't running. Re-run it anytime; it replaces itself cleanly.
        </div>
      </WStep>
      <ConnectionFootStep awStatus={awStatus} onCheck={onCheck} />
    </>
  );
}

function MacSteps({ awStatus, onCheck }) {
  return (
    <>
      <WStep n={1} title="Download ActivityWatch">
        <a href="https://activitywatch.net/downloads/" target="_blank" rel="noopener noreferrer" style={S.btnPrimarySm}>
          Open downloads page
        </a>
        <div style={S.wizardHint}>
          Grab the macOS <code style={S.code}>.dmg</code>, open it, drag ActivityWatch to Applications, then
          launch it once. Approve the macOS Accessibility prompt — that's how AW reads the active app/window.
        </div>
      </WStep>
      <WStep n={2} title="Install the browser extension (Chrome / Safari / Firefox)">
        <a href="https://chrome.google.com/webstore/detail/activitywatch-web-watcher/nglaklhklhcoonedhgnpgddginnjdadi"
           target="_blank" rel="noopener noreferrer" style={S.btnSm}>Chrome / Edge / Brave</a>
        <a href="https://addons.mozilla.org/en-US/firefox/addon/activitywatch-web-watcher/"
           target="_blank" rel="noopener noreferrer" style={{ ...S.btnSm, marginLeft: 8 }}>Firefox</a>
        <div style={S.wizardHint}>Optional but recommended for tab-level URL tracking.</div>
      </WStep>
      <WStep n={3} title="Allow ElecBid to read your activity (one-time)">
        <a href={SETUP_CMD_URL} download style={S.btnPrimarySm}>Download setup .command</a>
        <div style={S.wizardHint}>
          Right-click → <strong>Open</strong> the first time (Gatekeeper will warn — that's normal for unsigned scripts).
          Or in Terminal: <code style={S.code}>chmod +x ~/Downloads/elecbid-aw-setup.command && ~/Downloads/elecbid-aw-setup.command</code>
        </div>
      </WStep>
      <ConnectionFootStep awStatus={awStatus} onCheck={onCheck} />
    </>
  );
}

function AndroidSteps({ awStatus, onCheck }) {
  return (
    <>
      <div style={{ ...S.wizardHint, marginBottom: 16, padding: "10px 12px", background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.25)", borderRadius: 8 }}>
        On Android, ActivityWatch tracks per-app screen time using Android's Usage Stats permission. It's a separate
        install from the desktop version and syncs to a server you choose.
      </div>
      <WStep n={1} title="Install aw-android (beta)">
        <a href="https://play.google.com/store/apps/details?id=net.activitywatch.android"
           target="_blank" rel="noopener noreferrer" style={S.btnPrimarySm}>Open Play Store</a>
        <a href="https://github.com/ActivityWatch/aw-android/releases"
           target="_blank" rel="noopener noreferrer" style={{ ...S.btnSm, marginLeft: 8 }}>or APK</a>
        <div style={S.wizardHint}>Currently beta — Play Store version is the easiest path.</div>
      </WStep>
      <WStep n={2} title="Grant Usage Access permission">
        <div style={S.wizardHint}>
          On first launch the app will prompt you to enable <strong>Usage Access</strong> in Android Settings.
          Without it, Android won't tell the app which other apps you've used.
        </div>
      </WStep>
      <WStep n={3} title="Point it at your desktop AW (optional, recommended)">
        <div style={S.wizardHint}>
          In aw-android: Settings → Sync → enter the LAN IP of your Windows machine that runs AW (e.g. <code style={S.code}>http://192.168.1.42:5600</code>).
          Make sure both devices are on the same Wi-Fi. ElecBid pulls everything from the desktop server in one place.
        </div>
      </WStep>
      <WStep n={4} title="Or: log straight to ElecBid from your phone">
        <div style={S.wizardHint}>
          You can also just open ElecBid on your phone browser and use manual interval check-ins from there. The Day Audit page
          works on mobile and writes straight to Supabase — no AW needed.
        </div>
      </WStep>
      <ConnectionFootStep awStatus={awStatus} onCheck={onCheck} />
    </>
  );
}

function IosSteps({ onClose }) {
  return (
    <>
      <div style={{ ...S.wizardHint, marginBottom: 14, padding: "10px 12px", background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)", borderRadius: 8 }}>
        Apple doesn't let third-party apps read your app usage — Screen Time data is sandboxed.
        So there's no ActivityWatch for iPhone / iPad. You have two practical options:
      </div>
      <WStep n={1} title="Use manual check-ins from your phone browser">
        <div style={S.wizardHint}>
          Open ElecBid on your iPhone, head to Coach → Day Audit, and toggle "Ping me" with a 30–60 min interval.
          Notifications work in Safari (you may need to "Add to Home Screen" first). Each ping pops the typed/voice modal.
          The mic uses Apple's on-device speech recognition.
        </div>
        <button onClick={onClose} style={{ ...S.btnPrimarySm, marginTop: 8 }}>Got it</button>
      </WStep>
      <WStep n={2} title="For drives: a mileage app (Phase 2 import — coming)">
        <div style={S.wizardHint}>
          <a href="https://apps.apple.com/us/app/mileiq-mileage-tracker/id578830929" target="_blank" rel="noopener noreferrer" style={S.btnSm}>MileIQ</a>
          <a href="https://apps.apple.com/us/app/triplog-mileage-tracker/id518673000" target="_blank" rel="noopener noreferrer" style={{ ...S.btnSm, marginLeft: 8 }}>TripLog</a>
          <div style={{ marginTop: 6 }}>
            Auto-detects drives via GPS. We'll add a "Drives from MileIQ" CSV import to the Day Audit summary next.
          </div>
        </div>
      </WStep>
    </>
  );
}

function LinuxSteps({ awStatus, onCheck }) {
  return (
    <>
      <WStep n={1} title="Install ActivityWatch">
        <a href="https://activitywatch.net/downloads/" target="_blank" rel="noopener noreferrer" style={S.btnPrimarySm}>
          Downloads page
        </a>
        <div style={S.wizardHint}>
          Use the <code style={S.code}>.zip</code> bundle or your distro's package (AUR, deb, etc.). Run <code style={S.code}>aw-qt</code> to start.
        </div>
      </WStep>
      <WStep n={2} title="Allow ElecBid in aw-server config">
        <div style={S.wizardHint}>
          Edit <code style={S.code}>~/.config/activitywatch/aw-server-rust/config.toml</code> and add under <code style={S.code}>[server]</code>:
          <pre style={S.preBox}>{`cors_origins = "https://elec-bid.vercel.app"`}</pre>
          Then restart aw-qt. A Linux setup script will land in v2.
        </div>
      </WStep>
      <ConnectionFootStep awStatus={awStatus} onCheck={onCheck} />
    </>
  );
}

function WStep({ n, title, children }) {
  return (
    <div style={S.wizardStep}>
      <div style={S.wizardStepNum}>{n}</div>
      <div style={{ flex: 1 }}>
        <div style={S.wizardStepTitle}>{title}</div>
        <div style={S.wizardStepBody}>{children}</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SettingsPanel — category editor
// ═══════════════════════════════════════════════════════════════════════════
function SettingsPanel({ categories, userEmail, onClose, onReload }) {
  const [editing, setEditing] = useState({});
  const [newCat, setNewCat] = useState({ name: "", target_pct: "", color: "#a5ce3a", hourly_value: "" });
  const [saving, setSaving] = useState(false);

  async function saveOne(c) {
    setSaving(true);
    try {
      await apiFetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "upsert-category", ...c }),
      });
      await onReload();
      setEditing({});
    } finally { setSaving(false); }
  }
  async function removeOne(id) {
    if (!window.confirm("Delete this category?")) return;
    setSaving(true);
    try {
      await apiFetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "delete-category", id }),
      });
      await onReload();
    } finally { setSaving(false); }
  }
  async function addNew() {
    if (!newCat.name) return;
    setSaving(true);
    try {
      await apiFetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "upsert-category",
          user_email: userEmail,
          name: newCat.name,
          target_pct: newCat.target_pct ? Number(newCat.target_pct) / 100 : null,
          hourly_value: newCat.hourly_value ? Number(newCat.hourly_value) : null,
          color: newCat.color,
          sort_order: categories.length + 1,
        }),
      });
      await onReload();
      setNewCat({ name: "", target_pct: "", color: "#a5ce3a", hourly_value: "" });
    } finally { setSaving(false); }
  }

  return (
    <div style={S.modalBackdrop} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Categories</div>
          <button onClick={onClose} style={S.modalClose}>×</button>
        </div>
        <div style={S.modalBody}>
          <div style={{ fontSize: 12, color: "#8892a4", marginBottom: 12 }}>
            Edit your buckets and the % of your day each should ideally take. Hourly value lets Jai estimate the $ recovered if you delegate.
          </div>
          {categories.map(c => {
            const draft = editing[c.id] ?? {
              id: c.id,
              name: c.name,
              target_pct: c.target_pct != null ? (c.target_pct * 100).toFixed(0) : "",
              hourly_value: c.hourly_value ?? "",
              color: c.color || "#6b7280",
              sort_order: c.sort_order,
            };
            const dirty = !!editing[c.id];
            return (
              <div key={c.id} style={S.catRow}>
                <input type="color" value={draft.color}
                       onChange={e => setEditing({ ...editing, [c.id]: { ...draft, color: e.target.value } })}
                       style={S.colorInput} />
                <input value={draft.name}
                       onChange={e => setEditing({ ...editing, [c.id]: { ...draft, name: e.target.value } })}
                       style={{ ...S.txtInput, flex: 1 }} />
                <input type="number" min="0" max="100" placeholder="%" value={draft.target_pct}
                       onChange={e => setEditing({ ...editing, [c.id]: { ...draft, target_pct: e.target.value } })}
                       style={{ ...S.numInput, width: 60 }} />
                <input type="number" min="0" placeholder="$/hr" value={draft.hourly_value}
                       onChange={e => setEditing({ ...editing, [c.id]: { ...draft, hourly_value: e.target.value } })}
                       style={{ ...S.numInput, width: 70 }} />
                <button onClick={() => dirty && saveOne({
                  id: draft.id,
                  name: draft.name,
                  target_pct: draft.target_pct ? Number(draft.target_pct) / 100 : null,
                  hourly_value: draft.hourly_value ? Number(draft.hourly_value) : null,
                  color: draft.color,
                })} disabled={saving || !dirty} style={S.btnSm}>Save</button>
                <button onClick={() => removeOne(c.id)} disabled={saving} style={S.btnSmDanger}>×</button>
              </div>
            );
          })}
          <div style={{ marginTop: 18, fontSize: 12, color: "#8892a4", fontWeight: 700 }}>+ Add new category</div>
          <div style={S.catRow}>
            <input type="color" value={newCat.color}
                   onChange={e => setNewCat({ ...newCat, color: e.target.value })}
                   style={S.colorInput} />
            <input value={newCat.name} placeholder="Category name"
                   onChange={e => setNewCat({ ...newCat, name: e.target.value })}
                   style={{ ...S.txtInput, flex: 1 }} />
            <input type="number" min="0" max="100" placeholder="%" value={newCat.target_pct}
                   onChange={e => setNewCat({ ...newCat, target_pct: e.target.value })}
                   style={{ ...S.numInput, width: 60 }} />
            <input type="number" min="0" placeholder="$/hr" value={newCat.hourly_value}
                   onChange={e => setNewCat({ ...newCat, hourly_value: e.target.value })}
                   style={{ ...S.numInput, width: 70 }} />
            <button onClick={addNew} disabled={saving || !newCat.name} style={S.btnPrimarySm}>Add</button>
          </div>
        </div>
        <div style={S.modalFooter}>
          <button onClick={onClose} style={S.btnPrimary}>Done</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SlotModal — log what you did in this interval (typed or mic)
// ═══════════════════════════════════════════════════════════════════════════
function SlotModal({ slot, categories, onClose, onSave }) {
  const [text, setText] = useState("");
  const [category, setCategory] = useState("");
  const [delegatable, setDelegatable] = useState(false);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [source, setSource] = useState("typed");
  const recRef = useRef(null);

  const speechAvailable = !!getSR();

  function startListening() {
    const SR = getSR();
    if (!SR) return;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-CA";
    let finalAcc = "";
    rec.onresult = (e) => {
      let interimStr = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalAcc += e.results[i][0].transcript + " ";
        else interimStr += e.results[i][0].transcript;
      }
      if (finalAcc) {
        setText(prev => (prev + " " + finalAcc).trim());
        finalAcc = "";
      }
      setInterim(interimStr);
    };
    rec.onend = () => { setListening(false); setInterim(""); };
    rec.onerror = () => { setListening(false); setInterim(""); };
    rec.start();
    recRef.current = rec;
    setListening(true);
    setSource("mic");
  }
  function stopListening() {
    if (recRef.current) {
      try { recRef.current.stop(); } catch {}
      recRef.current = null;
    }
    setListening(false);
  }

  function save(skip = false) {
    if (skip) {
      onSave({
        slot_index: slot.slot_index,
        slot_start: slot.slot_start,
        slot_end: slot.slot_end,
        entry_text: null,
        entry_source: "skipped",
      });
      return;
    }
    onSave({
      slot_index: slot.slot_index,
      slot_start: slot.slot_start,
      slot_end: slot.slot_end,
      entry_text: text.trim(),
      entry_source: source,
      category: category || null,
      delegatable: delegatable,
    });
  }

  return (
    <div style={S.modalBackdrop}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>What were you doing?</div>
            <div style={{ fontSize: 12, color: "#8892a4" }}>{fmtTimeShort(slot.slot_start)}–{fmtTimeShort(slot.slot_end)}</div>
          </div>
          <button onClick={onClose} style={S.modalClose}>×</button>
        </div>
        <div style={S.modalBody}>
          <textarea
            value={text + (interim ? ` ${interim}` : "")}
            onChange={e => { setText(e.target.value); setSource("typed"); }}
            placeholder="One line. e.g. drafted EV charger quote for Smith, called supplier, wrote referral email…"
            style={S.textArea}
            autoFocus
          />
          <div style={{ display: "flex", gap: 10, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
            {speechAvailable && (
              listening ? (
                <button onClick={stopListening} style={S.btnRecording}>● Stop recording</button>
              ) : (
                <button onClick={startListening} style={S.btnSm}>🎤 Speak</button>
              )
            )}
            <select value={category} onChange={e => setCategory(e.target.value)} style={S.select}>
              <option value="">— pick category —</option>
              {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#e8eaf0" }}>
              <input type="checkbox" checked={delegatable} onChange={e => setDelegatable(e.target.checked)} />
              delegatable
            </label>
          </div>
        </div>
        <div style={S.modalFooter}>
          <button onClick={() => save(true)} style={S.btnSubtle}>Skip</button>
          <button onClick={() => save(false)} disabled={!text.trim()} style={S.btnPrimary}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Styles (inline only, per ElecBid convention)
// ═══════════════════════════════════════════════════════════════════════════
const S = {
  page: { padding: "24px 28px 60px", color: "#e8eaf0", fontFamily: "'Roboto', sans-serif", maxWidth: 1280, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16, marginBottom: 18 },
  backBtn: { background: "none", border: "none", color: "#a5ce3a", fontSize: 14, cursor: "pointer", padding: 0, marginBottom: 6, fontFamily: "inherit" },
  title: { fontSize: 28, fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.02em", lineHeight: 1.1 },
  subtitle: { fontSize: 13, color: "#8892a4", marginTop: 4, maxWidth: 540 },
  headerActions: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },

  errorBanner: { padding: "10px 14px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8, color: "#fca5a5", fontSize: 13, marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" },
  statusBanner: { padding: "10px 14px", background: "rgba(165,206,58,0.08)", border: "1px solid rgba(165,206,58,0.25)", borderRadius: 8, color: "#a5ce3a", fontSize: 13, marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" },
  bannerClose: { background: "none", border: "none", color: "inherit", fontSize: 18, cursor: "pointer", lineHeight: 1, padding: "0 0 0 12px" },

  connStrip: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "12px 16px", background: "#161a20", border: "1px solid", borderRadius: 10, marginBottom: 14 },

  startCard: { padding: 22, background: "linear-gradient(135deg, rgba(165,206,58,0.06), rgba(165,206,58,0.01))", border: "1px solid rgba(165,206,58,0.2)", borderRadius: 12, marginBottom: 16 },
  startCardTitle: { fontSize: 18, fontWeight: 700, color: "#e8eaf0", marginBottom: 6 },
  startCardDesc: { fontSize: 13, color: "#8892a4", lineHeight: 1.5, marginBottom: 14, maxWidth: 660 },
  startCardRow: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },

  toolbarCard: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, padding: "14px 16px", background: "#161a20", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, marginBottom: 14 },
  toolbarLeft: { display: "flex", gap: 22 },
  toolbarStat: { textAlign: "left" },
  toolbarStatNum: { fontSize: 22, fontWeight: 700, color: "#e8eaf0", lineHeight: 1 },
  toolbarStatLabel: { fontSize: 11, color: "#8892a4", marginTop: 2 },
  toolbarRight: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  pingToggle: { display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#e8eaf0", cursor: "pointer" },

  card: { background: "#161a20", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, marginBottom: 14, overflow: "hidden" },
  cardHeader: { padding: "14px 16px", borderBottom: "1px solid rgba(255,255,255,0.05)", display: "flex", justifyContent: "space-between", alignItems: "baseline" },
  cardTitle: { fontSize: 14, fontWeight: 700, color: "#e8eaf0" },
  cardSubtitle: { fontSize: 12, color: "#8892a4" },

  stackedBar: { display: "flex", height: 18, borderRadius: 9, overflow: "hidden", background: "rgba(255,255,255,0.04)" },
  legendRow: { display: "flex", alignItems: "center", gap: 8 },

  headline: { fontSize: 15, fontWeight: 700, color: "#e8eaf0", marginBottom: 14, padding: "10px 14px", background: "rgba(165,206,58,0.06)", border: "1px solid rgba(165,206,58,0.25)", borderRadius: 8, lineHeight: 1.4 },
  kdkGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 16 },
  kdkCol: { background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: 12 },
  kdkTitle: { fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 },
  kdkItem: { fontSize: 13, color: "#e8eaf0", padding: "6px 0", borderTop: "1px solid rgba(255,255,255,0.05)", lineHeight: 1.4 },
  hireCard: { padding: 14, background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.3)", borderRadius: 10, marginBottom: 14 },
  commentary: { fontSize: 13, color: "#cbd5e1", lineHeight: 1.55, paddingTop: 8 },

  blockRow: { display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: "1px solid rgba(255,255,255,0.04)" },

  pastDay: { padding: "12px 0", borderTop: "1px solid rgba(255,255,255,0.06)" },
  pastDayHeader: { display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" },
  driveRow: { display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 0", borderTop: "1px solid rgba(255,255,255,0.04)" },

  // Buttons
  btn: { padding: "8px 14px", background: "#1e2328", color: "#e8eaf0", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  btnPrimary: { padding: "8px 14px", background: "#a5ce3a", color: "#0d0f12", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  btnSm: { padding: "5px 10px", background: "#1e2328", color: "#e8eaf0", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", textDecoration: "none", display: "inline-block" },
  btnPrimarySm: { padding: "5px 10px", background: "#a5ce3a", color: "#0d0f12", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textDecoration: "none", display: "inline-block" },
  btnSmDanger: { padding: "5px 8px", background: "rgba(239,68,68,0.1)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  btnSubtle: { padding: "8px 14px", background: "transparent", color: "#8892a4", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  btnRecording: { padding: "5px 10px", background: "rgba(239,68,68,0.2)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.5)", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },

  // Inputs
  dateInput: { padding: "7px 10px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, color: "#e8eaf0", fontSize: 13, fontFamily: "inherit", colorScheme: "dark" },
  numInput: { padding: "7px 10px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, color: "#e8eaf0", fontSize: 13, fontFamily: "inherit", width: 80 },
  txtInput: { padding: "7px 10px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, color: "#e8eaf0", fontSize: 13, fontFamily: "inherit" },
  colorInput: { width: 30, height: 30, padding: 0, background: "transparent", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, cursor: "pointer" },
  select: { padding: "7px 10px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, color: "#e8eaf0", fontSize: 13, fontFamily: "inherit" },
  controlLabel: { fontSize: 12, color: "#8892a4", fontWeight: 600 },
  textArea: { width: "100%", minHeight: 100, padding: 12, background: "#0d1117", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, color: "#e8eaf0", fontSize: 14, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" },

  // Modals
  modalBackdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 },
  modal: { background: "#161a20", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, width: "100%", maxWidth: 600, maxHeight: "90vh", display: "flex", flexDirection: "column" },
  modalHeader: { padding: "14px 18px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center" },
  modalClose: { background: "none", border: "none", color: "#8892a4", fontSize: 22, cursor: "pointer", lineHeight: 1, padding: 0 },
  modalBody: { padding: 18, overflowY: "auto", flex: 1 },
  modalFooter: { padding: "12px 18px", borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", justifyContent: "flex-end", gap: 10 },

  // Wizard
  wizardP: { fontSize: 13, color: "#cbd5e1", lineHeight: 1.55, marginBottom: 16 },
  wizardStep: { display: "flex", gap: 12, padding: "12px 0", borderTop: "1px solid rgba(255,255,255,0.05)" },
  wizardStepNum: { width: 28, height: 28, borderRadius: "50%", background: "rgba(165,206,58,0.15)", color: "#a5ce3a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 },
  wizardStepTitle: { fontSize: 14, fontWeight: 700, color: "#e8eaf0", marginBottom: 6 },
  wizardStepBody: { fontSize: 13, color: "#cbd5e1" },
  wizardHint: { fontSize: 12, color: "#8892a4", marginTop: 6, lineHeight: 1.5 },
  code: { fontFamily: "monospace", background: "#0d1117", padding: "1px 5px", borderRadius: 3, fontSize: 12, color: "#a5ce3a" },
  preBox: { margin: "8px 0 0", padding: "8px 10px", background: "#0d1117", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 6, fontSize: 11, color: "#a5ce3a", fontFamily: "monospace", whiteSpace: "pre-wrap" },
  osTabs: { display: "flex", gap: 4, marginBottom: 14, flexWrap: "wrap" },
  osTabBtn: { padding: "5px 10px", background: "transparent", color: "#8892a4", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  osTabBtnActive: { background: "rgba(165,206,58,0.12)", color: "#a5ce3a", border: "1px solid rgba(165,206,58,0.3)" },

  // Settings
  catRow: { display: "flex", gap: 8, alignItems: "center", padding: "6px 0" },
};
